<template>
  <div class="createOrEditRoot">
    <div class="resourceFormPage divContainer genericResourceModule">
      <div v-if="loading" v-loading="loading" class="fullContainer" />
      <div v-else class="resourceFormPage__layout">
        <el-form
          ref="ruleForm"
          label-position="top"
          size="small"
          class="resourceFormPage__form"
          :model="ruleForm"
          :rules="formRules"
          :validate-on-rule-change="false"
        >
          <div class="resourceFormPage__headerDock">
            <header class="resourceFormPage__actionBar">
              <div class="resourceFormPage__lead">
                <h1 class="resourceFormPage__pageTitle">
                  {{ isCreate ? '创建资源' : '编辑资源' }}
                  <span
                    v-if="isCreate && completionPendingCount > 0"
                    class="resourceFormPage__pendingBadge"
                  >
                    {{ completionPendingCount }} 项待完成
                  </span>
                </h1>
                <span class="resourceFormPage__typeReadonly link-font-mono">{{ ruleForm.assetType }}</span>
              </div>
              <div class="resourceFormPage__actions">
                <button type="button" class="resourceFormPage__btn resourceFormPage__btn--ghost" @click="goBack">
                  取消
                </button>
                <button type="button" class="resourceFormPage__btn resourceFormPage__btn--primary" @click="submitForm">
                  保存
                </button>
              </div>
            </header>
          </div>

          <main ref="formMain" class="resourceFormPage__main main">
            <generic-resource-form-shell
              ref="formShell"
              :rule-form="ruleForm"
              :unlock-data="unlockData"
              :is-create="isCreate"
              :data-options="dataOptions"
              :show-completion-status="isCreate"
              :classify-type="classifyType"
              skip-type-selection
              :governance-profile="governanceProfile"
              @completion-changed="onCompletionChanged"
            />
          </main>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllCategory } from '../../../api/comment'
import { getAllDataList } from '../../../api/classifyManage'
import {
  addAiAssistant,
  updateAiAssistant,
  getAiAssistantById,
} from '../../../api/aiAssistant'
import GenericResourceFormShell from '../genericResourceManage/form/GenericResourceFormShell.vue'
import { computeGenericResourceCompletion } from '../genericResourceManage/form/genericResourceFormCompletion'
import {
  AI_ASSISTANT_ASSET_TYPE,
  AI_ASSISTANT_CLASSIFY_TYPE,
  AI_ASSISTANT_GOVERNANCE_PROFILE,
} from './aiAssistantConstants'
import {
  ensureAiAssistantJsonConfig,
  normalizeAiAssistantDetail,
} from './aiAssistantAdapter'
import {
  buildAiAssistantSavePayload,
  getAppVersion,
  mapCategoryNamesToIds,
  splitNeglectVersion,
  toAiAssistantFormData,
} from './aiAssistantSaveHelper'
import {
  buildAiAssistantLoadErrorMessage,
  buildAiAssistantSaveErrorMessage,
  buildAiAssistantValidationMessage,
} from './aiAssistantSaveErrorMessage'
import { resolveLayoutBackTo, buildLocationWithBackTo } from '../../../utils/layoutNavigation'

function createEmptyRuleForm() {
  return {
    resourceName: '',
    wallpaperName: '',
    assetType: AI_ASSISTANT_ASSET_TYPE,
    key: '',
    thumbUrl: '',
    comment: '',
    androidBeginVersion: '',
    androidOverVersion: '',
    iosBeginVersion: '',
    iosOverVersion: '',
    androidNeglectVersion: '',
    iosNeglectVersion: '',
    jsonConfig: ensureAiAssistantJsonConfig({}),
    categoryIdList: [],
    tagVOList: [],
    countryList: { isExclude: true, countries: [] },
    wallpaperAppVersionList: [{ start: '', end: '', skip: '', dataSetId: '' }],
  }
}

export default {
  name: 'AiAssistantCreateOrEdit',
  components: { GenericResourceFormShell },
  data() {
    return {
      isCreate: true,
      classifyType: AI_ASSISTANT_CLASSIFY_TYPE,
      unlockData: {
        dataSetLockGroupList: [{ lockGroupId: '', dataSetId: '' }],
        defaultLockGroupId: '',
      },
      ruleForm: createEmptyRuleForm(),
      tagMap: {},
      loading: false,
      dataOptions: [],
      completionRevision: 0,
      formBaseline: '',
      skipLeaveGuard: false,
      governanceProfile: AI_ASSISTANT_GOVERNANCE_PROFILE,
      formRules: {
        resourceName: [{ required: true, message: '运营名称不能为空', trigger: 'blur' }],
        'jsonConfig.aiRole.name': [{ required: true, message: '角色名称不能为空', trigger: 'blur' }],
        'jsonConfig.llmModel': [{ required: true, message: 'model 不能为空', trigger: 'change' }],
        'jsonConfig.prompt[0].content': [{ required: true, message: 'prompt 不能为空', trigger: 'blur' }],
        'jsonConfig.aiRole.avatar': [{ required: true, message: 'avatar 不能为空', trigger: 'change' }],
      },
    }
  },
  computed: {
    completionPendingCount() {
      void this.completionRevision
      const shell = this.$refs.formShell
      if (shell && shell.getCompletionSnapshot) {
        return shell.getCompletionSnapshot().pendingCount || 0
      }
      return computeGenericResourceCompletion({
        ruleForm: this.ruleForm,
        unlockData: this.unlockData,
        hasSelectedType: true,
        typeConfigSnapshot: {},
      }).pendingCount
    },
  },
  created() {
    this.isCreate = this.$route.path.indexOf('edit') === -1
    this.getCategoryInfo()
    this.getAllData()
    if (!this.isCreate) {
      this.loadDetail()
    } else {
      this.$nextTick(() => {
        this.markFormBaseline()
      })
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.skipLeaveGuard) {
      next()
      return
    }
    this.confirmLeaveIfDirty().then((ok) => {
      next(ok)
    })
  },
  methods: {
    captureFormSnapshot() {
      const ruleForm = JSON.parse(JSON.stringify(this.ruleForm))
      const shellSnap =
        this.$refs.formShell && this.$refs.formShell.captureSnapshot
          ? this.$refs.formShell.captureSnapshot()
          : {}
      return JSON.stringify({
        ruleForm,
        unlockData: this.unlockData,
        shellSnap,
      })
    },
    markFormBaseline() {
      this.formBaseline = this.captureFormSnapshot()
    },
    isFormDirty() {
      if (!this.formBaseline) {
        return false
      }
      return this.captureFormSnapshot() !== this.formBaseline
    },
    confirmLeaveIfDirty(message, options = {}) {
      const forRouteLeave = options.forRouteLeave !== false
      if (this.skipLeaveGuard || !this.isFormDirty()) {
        return Promise.resolve(true)
      }
      return this.$confirm(
        message || '当前页面有未保存的修改，离开后将丢失。确定要离开吗？',
        '未保存的更改',
        {
          confirmButtonText: forRouteLeave ? '离开' : '确定',
          cancelButtonText: '继续编辑',
          type: 'warning',
        }
      )
        .then(() => {
          if (forRouteLeave) {
            this.skipLeaveGuard = true
          }
          return true
        })
        .catch(() => false)
    },
    onCompletionChanged() {
      this.completionRevision += 1
    },
    getCategoryInfo(name = '') {
      getAllCategory(name, this.classifyType).then((res) => {
        const tagMap = {}
        if (res.success) {
          res.data.forEach((element) => {
            tagMap[element.name] = element.id
          })
          if (Object.keys(this.tagMap).length === 0) {
            this.tagMap = tagMap
          }
        }
      })
    },
    getAllData() {
      getAllDataList().then((res) => {
        if (res.success) {
          this.dataOptions = res.data
        }
      })
    },
    loadDetail() {
      this.loading = true
      getAiAssistantById({ id: this.$route.params.id })
        .then((res) => {
          if (!res.success || !res.data) {
            this.$message.error(buildAiAssistantLoadErrorMessage(res))
            return
          }
          const detail = normalizeAiAssistantDetail(res.data)
          detail.jsonConfig = ensureAiAssistantJsonConfig(detail.jsonConfig)

          this.ruleForm = {
            ...createEmptyRuleForm(),
            ...detail,
            resourceName: detail.resourceName || detail.wallpaperName || '',
            comment: detail.comment || '',
            androidBeginVersion: detail.androidBeginVersion || '',
            androidOverVersion: detail.androidOverVersion || '',
            iosBeginVersion: detail.iosBeginVersion || '',
            iosOverVersion: detail.iosOverVersion || '',
          }
          this.ruleForm.wallpaperAppVersionList = getAppVersion(
            detail.wallpaperAppVersionList && detail.wallpaperAppVersionList.length
              ? detail.wallpaperAppVersionList
              : [{ start: '', end: '', skip: '', dataSetId: '' }]
          )
          this.ruleForm.androidNeglectVersion = Array.isArray(detail.androidNeglectVersion)
            ? detail.androidNeglectVersion.join(',')
            : detail.androidNeglectVersion || ''
          this.ruleForm.iosNeglectVersion = Array.isArray(detail.iosNeglectVersion)
            ? detail.iosNeglectVersion.join(',')
            : detail.iosNeglectVersion || ''

          this.unlockData.dataSetLockGroupList =
            detail.wallpaperLockGroupVOList && detail.wallpaperLockGroupVOList.length
              ? detail.wallpaperLockGroupVOList
              : [{ lockGroupId: '', dataSetId: '' }]
          this.unlockData.defaultLockGroupId = detail.defaultLockGroupId || ''
          this.ruleForm.categoryIdList = (detail.categoryList || []).map((item) => item.name)
          this.$nextTick(() => {
            this.markFormBaseline()
          })
        })
        .catch((err) => {
          this.$message.error(buildAiAssistantLoadErrorMessage(err))
        })
        .finally(() => {
          this.loading = false
        })
    },
    submitForm() {
      const shell = this.$refs.formShell
      let shellError = null
      let mainValid = false

      const shellPromise =
        shell && shell.validateAll
          ? shell.validateAll().catch((err) => {
              const msg =
                typeof err === 'string' && err.trim()
                  ? err.trim()
                  : err && err.message && String(err.message).trim()
                    ? String(err.message).trim()
                    : ''
              shellError = msg || 'Ai 角色配置未填完整'
            })
          : Promise.resolve()

      const mainPromise = new Promise((resolve) => {
        this.$refs.ruleForm.validate((valid) => {
          mainValid = valid
          resolve()
        })
      })

      Promise.all([shellPromise, mainPromise]).then(() => {
        if (shellError || !mainValid) {
          const completion =
            shell && shell.getCompletionSnapshot
              ? shell.getCompletionSnapshot()
              : computeGenericResourceCompletion({
                  ruleForm: this.ruleForm,
                  unlockData: this.unlockData,
                  hasSelectedType: true,
                  typeConfigSnapshot: {},
                })
          const pendingCount = completion.pendingCount || 0

          this.$message({
            type: 'error',
            message: buildAiAssistantValidationMessage({
              shellError,
              pendingCount,
            }),
          })

          this.$nextTick(() => {
            if (shell && shell.scrollToFirstError) {
              const target = shell.scrollToFirstError()
              if (
                !target &&
                shellError &&
                (shell.typeConfigValidationFailed || shell.schemaValidationFailed) &&
                shell.scrollToTypeConfig
              ) {
                shell.scrollToTypeConfig()
              }
              return
            }
            if (
              shellError &&
              shell &&
              (shell.typeConfigValidationFailed || shell.schemaValidationFailed) &&
              shell.scrollToTypeConfig
            ) {
              shell.scrollToTypeConfig()
            }
          })
          return
        }

        Object.assign(this.ruleForm, this.unlockData)
        this.ruleForm.dataSetLockGroupList = (this.ruleForm.dataSetLockGroupList || []).filter(
          (row) => row.lockGroupId !== '' && row.dataSetId !== ''
        )
        this.persist()
      })
    },
    persist() {
      const oldCategoryData = JSON.parse(JSON.stringify(this.ruleForm.categoryIdList))
      const oldAndroidVersion = this.ruleForm.androidNeglectVersion
      const oldIosVersion = this.ruleForm.iosNeglectVersion

      this.ruleForm.categoryIdList = mapCategoryNamesToIds(this.ruleForm.categoryIdList, this.tagMap)
      this.ruleForm.androidNeglectVersion = splitNeglectVersion(this.ruleForm.androidNeglectVersion)
      this.ruleForm.iosNeglectVersion = splitNeglectVersion(this.ruleForm.iosNeglectVersion)
      this.ruleForm.wallpaperName = this.ruleForm.resourceName

      const payload = buildAiAssistantSavePayload(this.ruleForm, this.unlockData, {
        id: this.isCreate ? undefined : this.$route.params.id,
      })
      const form = toAiAssistantFormData(payload)
      const request = this.isCreate ? addAiAssistant(form) : updateAiAssistant(form)

      const restoreFormBeforeSave = () => {
        // categoryIdList 已被改成逗号串，失败必须还原，否则分类选择器绑不住
        this.ruleForm.categoryIdList = oldCategoryData
        this.ruleForm.androidNeglectVersion = oldAndroidVersion
        this.ruleForm.iosNeglectVersion = oldIosVersion
      }

      request
        .then((response) => {
          if (!response || !response.success) {
            // resolveErrorDataOnBizError：500 也会进 then；统一走 catch 拼「新建/保存失败：…」
            return Promise.reject(response || { errMessage: '' })
          }
          this.skipLeaveGuard = true
          this.$message.success(this.isCreate ? '新建成功' : '保存成功')
          if (this.isCreate) {
            const resourceId = response.data
            if (resourceId != null && resourceId !== '') {
              this.$router.push(
                buildLocationWithBackTo(
                  `/resource/aiAssistant/info/${resourceId}`,
                  this.$route.query && this.$route.query.backTo
                )
              )
            } else {
              const backTo = resolveLayoutBackTo(this.$route, '/resource/aiAssistant/list')
              this.$router.push(backTo)
            }
          } else {
            this.$router.push(
              buildLocationWithBackTo(
                `/resource/aiAssistant/info/${this.$route.params.id}`,
                this.$route.query && this.$route.query.backTo
              )
            )
          }
          return response
        })
        .catch((err) => {
          restoreFormBeforeSave()
          this.$message.error(
            buildAiAssistantSaveErrorMessage({
              isCreate: this.isCreate,
              err,
            })
          )
        })
    },
    goBack() {
      this.confirmLeaveIfDirty().then((ok) => {
        if (!ok) {
          return
        }
        const backTo = resolveLayoutBackTo(this.$route, '/resource/aiAssistant/list')
        if (backTo) {
          this.$router.push(backTo)
        } else {
          this.$router.back(-1)
        }
      })
    },
  },
}
</script>

<style scoped>
@import '../genericResourceManage/resourceFormPage.css';

.divContainer,
.resourceFormPage__layout {
  height: 100%;
}

.fullContainer {
  width: 100%;
  height: 100%;
}
</style>
